import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAddToCart } from '@quizzach/api-hooks';
import {
	EnumQuizType,
	quickQuestionTypes,
	RoutePath,
} from '@quizzach/interfaces';
import {
	AuthQuizCreation,
	Navigation,
	PoolExhaustionWarningModal,
	useToast,
} from '@quizzach/components';
import { useAuth, useCookieConsent } from '@quizzach/contexts';
import { useToastClearance } from '@quizzach/hooks';
import { scrollToTop } from '@quizzach/utils';
import { Reveal } from '@quizzach/motion';
import {
	IcArrowRight,
	IcCheck,
	QtAbcd,
	QtAssociation,
	QtConnection,
	QtStandard,
	QtVisual,
} from '@quizzach/assets';
import {
	BqNums,
	BqPage,
	BqSec,
	BqTypes,
	BtnSpinner,
	BtnTeal,
	CfgHead,
	KBar,
	KTick,
} from '@quizzach/styles';

const NUMBER_OPTIONS = [20, 30, 40, 50];
const FIXED_TYPES = ['standard', 'abcd'];
const ALL_TYPES = ['standard', 'abcd', 'connection', 'association', 'visual'];

const TYPE_ILLUSTRATIONS: Record<
	string,
	React.ComponentType<{ className?: string }>
> = {
	standard: QtStandard,
	abcd: QtAbcd,
	connection: QtConnection,
	association: QtAssociation,
	visual: QtVisual,
};

const readStoredQuickQuiz = () => {
	try {
		return JSON.parse(sessionStorage.getItem('quickQuiz') || '{}');
	} catch {
		return {};
	}
};

/**
 * Kviz opceg znanja, one page: question count (giant numbers with the teal
 * highlighter) + question types (tekstualna and ponudjeni odgovori always in
 * the quiz). Keeps the quickQuiz sessionStorage shape
 * ({ number_of_questions, question_types }) and useAddToCart({type:'quick'}).
 */
export const QuickPick = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	const { isAuthenticated, profileInfo, refreshProfile } = useAuth();
	const { hasConsent } = useCookieConsent();

	const stored = useMemo(readStoredQuickQuiz, []);
	const [questionsNum, setQuestionsNum] = useState<number>(
		NUMBER_OPTIONS.includes(stored.number_of_questions)
			? stored.number_of_questions
			: 30
	);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(
		Array.isArray(stored.question_types) && stored.question_types.length > 0
			? stored.question_types
			: [...ALL_TYPES]
	);

	const [showAuthModal, setShowAuthModal] = useState(false);
	const [showPoolWarningModal, setShowPoolWarningModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const { addToCart } = useAddToCart({ type: 'quick' });
	const { showToast } = useToast();
	const kbarRef = useToastClearance();

	useEffect(() => {
		scrollToTop();
	}, []);

	// Only show the auth modal once cookie consent has been handled.
	useEffect(() => {
		if (isAuthenticated === false && hasConsent) {
			setShowAuthModal(true);
		} else {
			setShowAuthModal(false);
		}
	}, [isAuthenticated, hasConsent]);

	useEffect(() => {
		let isMounted = true;

		const checkPoolExhaustionWarning = async () => {
			if (!isAuthenticated) return;

			const freshProfileInfo = await refreshProfile();

			if (isMounted && freshProfileInfo?.pool_exhaustion_warning) {
				setShowPoolWarningModal(true);
			}
		};

		checkPoolExhaustionWarning();

		return () => {
			isMounted = false;
		};
	}, [isAuthenticated, refreshProfile]);

	// Keep the sessionStorage contract in sync with both selections.
	useEffect(() => {
		const existing = readStoredQuickQuiz();
		sessionStorage.setItem(
			'quickQuiz',
			JSON.stringify({
				...existing,
				number_of_questions: questionsNum,
				question_types: selectedTypes,
			})
		);
	}, [questionsNum, selectedTypes]);

	const toggleType = (type: string) => {
		if (FIXED_TYPES.includes(type)) return;
		setSelectedTypes((prev) =>
			prev.includes(type)
				? prev.filter((item) => item !== type)
				: [...prev, type]
		);
	};

	const onTypeKeyDown = (
		event: React.KeyboardEvent<HTMLDivElement>,
		type: string
	) => {
		if (event.key === 'Enter' || event.key === ' ') {
			// Space would otherwise scroll the page
			event.preventDefault();
			toggleType(type);
		}
	};

	const handleAddToCart = async () => {
		if (submitting) return;
		setSubmitting(true);
		const result = await addToCart();
		setSubmitting(false);
		if (result.success) {
			navigate(RoutePath.QuickSummary, {
				state: { source: EnumQuizType.quick },
			});
		} else if (result.conflict) {
			showToast({
				severity: 'error',
				message: t('toast.problem'),
				description: t('error.conflict'),
			});
		} else if (!result.redirectedToLogin) {
			// logging in is part of the flow, not an error — after login the user
			// lands back here with their picks restored and continues themselves
			showToast({
				severity: 'error',
				message: t('toast.problem'),
				description: t('toast.notAddedText'),
			});
		}
	};

	return (
		<BqPage>
			<PoolExhaustionWarningModal
				show={showPoolWarningModal}
				onConfirm={() => setShowPoolWarningModal(false)}
				onCancel={() => navigate(RoutePath.PremadeQuizzes)}
				text={t('quiz.quick.poolWarning', {
					nickname: `${profileInfo?.nickname
						.charAt(0)
						.toUpperCase()}${profileInfo?.nickname.slice(1)}`,
				})}
				cancelLabel={t('quiz.quick.viewSpecialEditions')}
				confirmLabel={t('quiz.quick.continue')}
			/>
			{showAuthModal && (
				<AuthQuizCreation
					closeModal={() => setShowAuthModal(false)}
					path={location.pathname}
					source='quick-init'
				/>
			)}

			<Navigation />

			<CfgHead>
				{/* homepage-hero treatment: title lines rise out of an overflow
				    clip on load (styles live on CfgHead) */}
				<h1>
					<span className='row'>
						<span>{t('quiz.quick.redesign.title1')}</span>
					</span>
					<span className='row'>
						<span>
							<em>{t('quiz.quick.redesign.title2')}</em>
						</span>
					</span>
				</h1>
				<span className='hand'>{t('quiz.quick.redesign.hand')}</span>
			</CfgHead>

			<Reveal>
				<BqSec>
					<div className='bq-lab'>{t('quiz.creation.questionCount')}</div>
					<BqNums>
						{NUMBER_OPTIONS.map((num) => (
							<button
								key={num}
								type='button'
								className={questionsNum === num ? 'on' : ''}
								onClick={() => setQuestionsNum(num)}
							>
								<span>{num}</span>
							</button>
						))}
					</BqNums>
				</BqSec>
			</Reveal>

			<Reveal delay={1}>
				<BqSec>
					<div className='bq-lab'>{t('quiz.quick.type.title2')}</div>
					<BqTypes>
						{quickQuestionTypes.map((item) => {
							const fixed = FIXED_TYPES.includes(item.type);
							const on = selectedTypes.includes(item.type);
							const Illustration = TYPE_ILLUSTRATIONS[item.type];

							return (
								<div
									key={item.type}
									className={`row ${fixed ? 'fixed' : ''} ${on ? '' : 'off'}`}
									role='checkbox'
									aria-checked={on}
									aria-disabled={fixed}
									tabIndex={fixed ? -1 : 0}
									onClick={() => toggleType(item.type)}
									onKeyDown={(event) => onTypeKeyDown(event, item.type)}
								>
									<KTick
										as='span'
										$on={on}
										className={fixed ? 'tick-fixed' : ''}
									>
										<IcCheck size={16} />
									</KTick>
									<span className='tname'>{t(item.text)}</span>
									{fixed && (
										<span className='bq-note'>
											{t('quiz.quick.redesign.alwaysIncluded')}
										</span>
									)}
									{Illustration && <Illustration className='qtill' />}
								</div>
							);
						})}
					</BqTypes>
				</BqSec>
			</Reveal>

			<KBar ref={kbarRef}>
				<div className='left'>
					<span className='tot ok'>
						{t('quiz.quick.redesign.totalLabelPre')} <em>{questionsNum}</em>{' '}
						{t('quiz.quick.redesign.totalLabelPost')}
					</span>
				</div>
				<div className='right'>
					<BtnTeal
						type='button'
						$small
						aria-busy={submitting}
						onClick={handleAddToCart}
					>
						{t('quiz.quick.redesign.toCart')}{' '}
						{submitting ? <BtnSpinner /> : <IcArrowRight size={16} />}
					</BtnTeal>
				</div>
			</KBar>
		</BqPage>
	);
};
